import { useEffect } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { BulkResultsSummaryProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

export function BulkResultsSummary({
  open,
  results,
  onRetryFailed,
  onClose,
  className,
}: BulkResultsSummaryProps) {
  const { t } = useMasterDocumentTranslation();
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const successCount = results.filter((r) => r.status === 'success').length;
  const partialCount = results.filter((r) => r.status === 'partial').length;
  const failedCount = results.filter((r) => r.status === 'failed').length;
  const failedIds = results.filter((r) => r.status === 'failed').map((r) => r.sourceDocumentId);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'max-w-2xl w-[calc(100%-32px)]',
          'bg-bg-primary dark:bg-dark-bg-primary',
          'rounded-2xl',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:shadow-none dark:border dark:border-dark-border-primary',
          'overflow-hidden',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-results-title"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border-primary dark:border-dark-border-primary">
          <h2
            id="bulk-results-title"
            className="flex-1 text-base font-bold text-content-primary dark:text-dark-content-primary"
          >
            {t('master_document.crossing.bulkResultsTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'p-1 rounded-md transition-colors',
              'text-content-secondary hover:bg-background-secondary',
              'dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
            aria-label={t('master_document.chrome.close')}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Summary chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              {t('master_document.crossing.bulkSuccessCount', { count: successCount })}
            </span>
            {partialCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                {t('master_document.crossing.bulkPartialCount', { count: partialCount })}
              </span>
            )}
            {failedCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {t('master_document.crossing.bulkWithErrorCount', { count: failedCount })}
              </span>
            )}
          </div>

          {/* Results table */}
          <div className="overflow-auto max-h-72 rounded-lg border border-border-primary dark:border-dark-border-primary">
            <table className="w-full text-sm">
              <thead className="bg-background-secondary dark:bg-dark-bg-secondary sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary">
                    {t('master_document.crossing.bulkColumn.sourceDocument')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary">
                    {t('master_document.crossing.bulkColumn.result')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary">
                    {t('master_document.crossing.bulkColumn.generatedDocument')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary">
                    {t('master_document.chrome.error')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, idx) => (
                  <tr
                    key={result.sourceDocumentId}
                    className={clsx(
                      'border-t border-border-primary dark:border-dark-border-primary',
                      idx % 2 === 0 ? 'bg-bg-primary dark:bg-dark-bg-primary' : 'bg-background-secondary dark:bg-dark-bg-secondary',
                    )}
                  >
                    <td className="px-3 py-2.5 text-sm text-content-primary dark:text-dark-content-primary font-medium whitespace-nowrap">
                      {result.sourceDocumentNumber ?? result.sourceDocumentId}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {result.status === 'success' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {t('master_document.crossing.bulkStatus.success')}
                        </span>
                      ) : result.status === 'partial' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          {t('master_document.lineFulfillment.partial')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          {t('master_document.chrome.error')}
                        </span>
                      )}
                    </td>
                    {/*
                      Número del documento hijo. NO es accionable: no tiene `onClick` ni lo
                      tuvo nunca, así que el `cursor-pointer` + `hover:underline` que llevaba
                      era una afordancia falsa — se leía como un enlace que al pulsar no hacía
                      nada. Se pinta como el dato que es, con la cifra tabular que el canon
                      usa para códigos.
                    */}
                    <td className="px-3 py-2.5">
                      {result.status !== 'failed' && result.childDocumentNumber ? (
                        <span className="font-medium text-sm tabular-nums text-content-primary dark:text-dark-content-primary">
                          {result.childDocumentNumber}
                        </span>
                      ) : (
                        <span className="text-content-secondary dark:text-dark-content-secondary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 max-w-[200px]">
                      {result.status === 'partial' && result.blockedLines && result.blockedLines.length > 0 ? (
                        <span
                          className="text-xs text-amber-600 dark:text-amber-400 truncate block max-w-[200px]"
                          title={t('master_document.crossing.blockedLinesCount', {
                            count: result.blockedLines.length,
                          })}
                        >
                          {t('master_document.crossing.bulkBlockedLines', { count: result.blockedLines.length })}
                        </span>
                      ) : result.status === 'failed' && result.errorMessage ? (
                        <span
                          className="text-xs text-red-600 dark:text-red-400 truncate block max-w-[200px]"
                          title={result.errorMessage}
                        >
                          {result.errorMessage}
                        </span>
                      ) : (
                        <span className="text-content-secondary dark:text-dark-content-secondary">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Retry section */}
          {failedCount > 0 && (
            <div className="flex items-center justify-between gap-3 pt-1 border-t border-border-primary dark:border-dark-border-primary">
              <span className="text-sm text-content-secondary dark:text-dark-content-secondary">
                {t('master_document.crossing.bulkSelectFailed')}
              </span>
              <button
                type="button"
                onClick={() => onRetryFailed(failedIds)}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
                  'bg-primary-inverse-background text-primary-inverse-content hover:bg-primary-custom-700',
                  'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
                  'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                  'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                )}
              >
                {t('master_document.crossing.bulkRetryFailed', { count: failedCount })}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-border-primary dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold transition-colors',
              'border border-border-primary text-content-secondary hover:bg-background-secondary',
              'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            {t('master_document.chrome.close')}
          </button>
        </div>
      </div>
    </>
  );
}
